import React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { API_REQUESTS_PAUSED } from '../../data'
import { getApplications } from '../../services/admin/applicationsApi'
import { getPermissions } from '../../services/admin/permissionsApi'
import { createRole, deleteRole, getRole, getRoles, updateRole } from '../../services/admin/rolesApi'
import { formatDateTime, getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'
import { useAuth } from '../../context/AuthContext'
import { getAdminApplicationCode, tienePermiso } from '../../utils/permisos'
import { AdminPermissions } from './AdminPermissions'
import { SortableHeader } from '../../components/tables/SortableHeader'
import { sortTableRows, toggleTableSort } from '../../utils/tableSorting'

const emptyRoleForm = {
  applicationId: '',
  code: '',
  name: '',
  description: '',
  active: true,
  allWarehouses: true,
  permissionIds: [],
  warehouseIdsText: '',
}

const getPermissionItem = (permission) => permission.permission || permission

const getPermissionId = (permission) => String(
  permission.permissionId
  || permission.permission?.id
  || permission.id
  || '',
)

const getWarehouseId = (warehouse) => String(
  warehouse.warehouseId
  || warehouse.warehouse?.id
  || warehouse.id
  || '',
)

const getRolePermissions = (role) => role.permissions || role.rolePermissions || []

const getRoleUsers = (role) => role.users || role.userRoles || []

const getRoleWarehouses = (role) => role.warehouses || role.roleWarehouses || []

const mapApiRole = (role) => ({
  id: role.id,
  name: role.name,
  code: role.code,
  description: role.description || '-',
  active: role.active !== false,
  isSystem: Boolean(role.isSystem),
  app: role.application?.code || role.applicationCode || role.applicationId || '-',
  appName: role.application?.name || role.application?.code || role.applicationId || '-',
  users: role.usersCount ?? getRoleUsers(role).length ?? 0,
  permissions: getRolePermissions(role).length,
  scope: role.allWarehouses ? 'Todos los almacenes' : `${getRoleWarehouses(role).length || 0} almacenes`,
  icon: <SecurityRoundedIcon />,
  raw: role,
})

const getOptionLabel = (option) => option.name || option.code || option.id

const buildRoleMetrics = (roles) => {
  const permissionCount = roles.reduce((total, role) => total + Number(role.permissions || 0), 0)
  const applicationsCount = new Set(roles.map((role) => role.app).filter((app) => app && app !== '-')).size
  const scopedRoles = roles.filter((role) => role.scope && role.scope !== 'Todos los almacenes').length

  return [
    { title: 'Roles definidos', value: String(roles.length), icon: <SecurityRoundedIcon /> },
    { title: 'Permisos', value: String(permissionCount), icon: <SecurityRoundedIcon /> },
    { title: 'Apps con roles', value: String(applicationsCount), icon: <SecurityRoundedIcon /> },
    { title: 'Alcances limitados', value: String(scopedRoles), icon: <SecurityRoundedIcon /> },
  ]
}

const buildRoleFormFromRole = (role) => ({
  applicationId: String(role.applicationId || role.application?.id || ''),
  code: role.code || '',
  name: role.name || '',
  description: role.description === '-' ? '' : role.description || '',
  active: role.active !== false,
  allWarehouses: role.allWarehouses !== false,
  permissionIds: getRolePermissions(role).map(getPermissionId).filter(Boolean),
  warehouseIdsText: getRoleWarehouses(role).map(getWarehouseId).filter(Boolean).join(', '),
})

const buildWarehousePayload = (warehouseIdsText) => warehouseIdsText
  .split(',')
  .map((warehouseId) => warehouseId.trim())
  .filter(Boolean)
  .map((warehouseId) => ({ warehouseId }))

const buildRolePayload = (form, includeApplication = false) => ({
  ...(includeApplication ? { applicationId: form.applicationId } : {}),
  code: form.code.trim().toUpperCase(),
  name: form.name.trim(),
  ...(form.description.trim() ? { description: form.description.trim() } : { description: '' }),
  active: form.active,
  allWarehouses: form.allWarehouses,
  permissions: form.permissionIds.map((permissionId) => ({ permissionId })),
  warehouses: form.allWarehouses ? [] : buildWarehousePayload(form.warehouseIdsText),
})

const validateRoleForm = (form, requireApplication) => {
  if (requireApplication && !form.applicationId) return 'La aplicación es obligatoria.'
  if (!form.code.trim()) return 'El código es obligatorio.'
  if (!form.name.trim()) return 'El nombre es obligatorio.'
  if (!form.allWarehouses && buildWarehousePayload(form.warehouseIdsText).length === 0) {
    return 'Indica al menos un ID de almacén o marca el alcance como todos los almacenes.'
  }
  return ''
}

const loadRoleOptions = async () => {
  const [applicationsResponse, permissionsResponse] = await Promise.all([
    getApplications({ page: 1, limit: 500, sortDirection: 'asc' }),
    getPermissions({ page: 1, limit: 500, sortDirection: 'asc' }),
  ])

  return {
    applications: getPaginatedItems(applicationsResponse).filter((application) => application.active !== false),
    permissions: getPaginatedItems(permissionsResponse).filter((permission) => permission.active !== false),
  }
}

const RoleInfoItem = ({ label, value }) => (
  <Box className="admin-user-info-item">
    <Typography>{label}</Typography>
    <Typography>{value || '-'}</Typography>
  </Box>
)

const RoleFormFields = ({ form, applications, permissions, editingApplication, onChange, onPermissionSelect }) => {
  const applicationPermissions = permissions.filter((permission) => (
    String(permission.application_id ?? permission.applicationId) === String(form.applicationId)
  ))
  const findPermission = (permissionId) => applicationPermissions.find((permission) => String(permission.id) === String(permissionId))

  return (
    <Stack spacing={2}>
      <Box className="admin-create-user-grid">
        <FormControl size="small" required disabled={editingApplication}>
          <InputLabel>Aplicación</InputLabel>
          <Select name="applicationId" value={form.applicationId} label="Aplicación" onChange={onChange}>
            {applications.map((application) => (
              <MenuItem key={application.id} value={String(application.id)}>
                {application.name || application.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Código" name="code" value={form.code} onChange={onChange} size="small" required />
        <TextField label="Nombre" name="name" value={form.name} onChange={onChange} size="small" required />
        <TextField label="Descripción" name="description" value={form.description} onChange={onChange} size="small" />
        <FormControlLabel control={<Checkbox checked={form.active} onChange={onChange} name="active" />} label="Rol activo" />
        <FormControlLabel control={<Checkbox checked={form.allWarehouses} onChange={onChange} name="allWarehouses" />} label="Todos los almacenes" />
      </Box>

      {!form.allWarehouses && (
        <TextField
          label="IDs de almacén"
          name="warehouseIdsText"
          value={form.warehouseIdsText}
          onChange={onChange}
          size="small"
          helperText="Introduce IDs separados por coma."
          fullWidth
        />
      )}

      <Box className="admin-user-dialog-section">
        <Typography className="section-title">Permisos</Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Permisos asignados</InputLabel>
          <Select
            multiple
            value={form.permissionIds}
            label="Permisos asignados"
            onChange={onPermissionSelect}
            renderValue={(selected) => selected.map((permissionId) => getOptionLabel(findPermission(permissionId) || { id: permissionId })).join(', ')}
          >
            {applicationPermissions.map((permission) => (
              <MenuItem key={permission.id} value={String(permission.id)}>
                <Checkbox checked={form.permissionIds.includes(String(permission.id))} />
                <Box>
                  <Typography className="admin-create-user-option-title">{permission.name || permission.code}</Typography>
                  <Typography className="admin-create-user-option-copy">{permission.code || permission.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Stack>
  )
}

const CreateRoleDialog = ({ open, onClose, onCreateRole }) => {
  const [form, setForm] = React.useState(emptyRoleForm)
  const [applications, setApplications] = React.useState([])
  const [permissions, setPermissions] = React.useState([])
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [optionsError, setOptionsError] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) return undefined

    let active = true
    setForm(emptyRoleForm)
    setSubmitError('')
    setOptionsError('')

    if (API_REQUESTS_PAUSED) return undefined

    setLoadingOptions(true)
    loadRoleOptions()
      .then(({ applications: applicationOptions, permissions: permissionOptions }) => {
        if (!active) return
        setApplications(applicationOptions)
        setPermissions(permissionOptions)
      })
      .catch((error) => {
        if (active) setOptionsError(getApiErrorMessage(error, 'No se han podido cargar aplicaciones y permisos.'))
      })
      .finally(() => {
        if (active) setLoadingOptions(false)
      })

    return () => {
      active = false
    }
  }, [open])

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'applicationId' ? { permissionIds: [] } : {}),
    }))
  }

  const handlePermissionSelect = (event) => {
    setForm((currentForm) => ({ ...currentForm, permissionIds: event.target.value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError('')

    const validationMessage = validateRoleForm(form, true)
    if (validationMessage) {
      setSubmitError(validationMessage)
      return
    }

    setSubmitting(true)
    try {
      await onCreateRole(buildRolePayload(form, true))
      onClose()
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'No se ha podido crear el rol.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle className="admin-user-dialog-title">
          <Box>
            <Typography variant="h5">Nuevo rol</Typography>
            <Typography>Alta de rol, aplicación, permisos y alcance operativo.</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {loadingOptions && <LinearProgress />}
            {optionsError && <Alert severity="warning">{optionsError}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <RoleFormFields
              form={form}
              applications={applications}
              permissions={permissions}
              onChange={handleChange}
              onPermissionSelect={handlePermissionSelect}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={submitting || loadingOptions}>
            {submitting ? 'Creando...' : 'Crear rol'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const RoleDetailDialog = ({ role, open, loading, errorMessage, onClose, onUpdateRole, onDeleteRole }) => {
  const selectedRole = role?.raw || role
  const [form, setForm] = React.useState(emptyRoleForm)
  const [applications, setApplications] = React.useState([])
  const [permissions, setPermissions] = React.useState([])
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [submitError, setSubmitError] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState('')
  const [deleting, setDeleting] = React.useState(false)

  React.useEffect(() => {
    if (!open || !selectedRole) return undefined

    let active = true
    setForm(buildRoleFormFromRole(selectedRole))
    setSubmitError('')
    setDeleteError('')
    setConfirmDelete(false)

    if (API_REQUESTS_PAUSED) return undefined

    setLoadingOptions(true)
    loadRoleOptions()
      .then(({ applications: applicationOptions, permissions: permissionOptions }) => {
        if (!active) return
        setApplications(applicationOptions)
        setPermissions(permissionOptions)
      })
      .catch((error) => {
        if (active) setSubmitError(getApiErrorMessage(error, 'No se han podido cargar aplicaciones y permisos.'))
      })
      .finally(() => {
        if (active) setLoadingOptions(false)
      })

    return () => {
      active = false
    }
  }, [open, selectedRole])

  if (!selectedRole) return null

  const application = selectedRole.application || {}
  const rolePermissions = getRolePermissions(selectedRole)
  const users = getRoleUsers(selectedRole)
  const warehouses = getRoleWarehouses(selectedRole)
  const isSystemRole = Boolean(selectedRole.isSystem)

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handlePermissionSelect = (event) => {
    setForm((currentForm) => ({ ...currentForm, permissionIds: event.target.value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError('')

    const validationMessage = validateRoleForm(form, false)
    if (validationMessage) {
      setSubmitError(validationMessage)
      return
    }

    setSubmitting(true)
    try {
      await onUpdateRole(selectedRole.id, buildRolePayload(form, false))
      onClose()
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'No se ha podido actualizar el rol.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    setDeleteError('')
    setDeleting(true)
    try {
      await onDeleteRole(selectedRole.id)
      onClose()
    } catch (error) {
      setDeleteError(getApiErrorMessage(error, 'No se ha podido eliminar el rol.'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle className="admin-user-dialog-title">
          <Box>
            <Typography variant="h5">{selectedRole.name || role.name}</Typography>
            <Typography>{selectedRole.code || role.code}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {loading && <LinearProgress />}
            {loadingOptions && <LinearProgress />}
            {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Información principal</Typography>
              <Box className="admin-user-info-grid">
                <RoleInfoItem label="ID" value={selectedRole.id} />
                <RoleInfoItem label="Aplicación" value={application.name || application.code || selectedRole.applicationId} />
                <RoleInfoItem label="Rol de sistema" value={isSystemRole ? 'Sí' : 'No'} />
                <RoleInfoItem label="Usuarios" value={users.length || role.users || 0} />
                <RoleInfoItem label="Permisos actuales" value={rolePermissions.length || role.permissions || 0} />
                <RoleInfoItem label="Alcance actual" value={selectedRole.allWarehouses ? 'Todos los almacenes' : `${warehouses.length} almacenes`} />
                <RoleInfoItem label="Creado" value={formatDateTime(selectedRole.createdAt, '-')} />
                <RoleInfoItem label="Actualizado" value={formatDateTime(selectedRole.updatedAt, '-')} />
              </Box>
            </Box>

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Editar rol</Typography>
              <RoleFormFields
                form={form}
                applications={applications.length > 0 ? applications : [application].filter((item) => item?.id)}
                permissions={permissions}
                editingApplication
                onChange={handleChange}
                onPermissionSelect={handlePermissionSelect}
              />
            </Box>

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Permisos asignados en backend</Typography>
              <Stack direction="row" spacing={0.8} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {rolePermissions.length === 0 && <Typography className="admin-user-empty">Sin permisos asignados en el detalle recibido.</Typography>}
                {rolePermissions.map((permission) => {
                  const item = getPermissionItem(permission)
                  return (
                    <Chip
                      key={item.id || item.code || permission.permissionId}
                      size="small"
                      className="suite-status-chip"
                      label={item.code || item.name || permission.permissionId}
                    />
                  )
                })}
              </Stack>
            </Box>

            <Box className="admin-user-dialog-section admin-user-danger-zone">
              <Box>
                <Typography className="section-title">Eliminar rol</Typography>
                <Typography className="admin-user-section-copy">
                  {isSystemRole
                    ? 'Este rol es de sistema y no se puede eliminar desde la interfaz.'
                    : 'Esta acción eliminará el rol y sus asignaciones asociadas.'}
                </Typography>
                {deleteError && <Alert severity="error">{deleteError}</Alert>}
              </Box>
              {!confirmDelete ? (
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={() => setConfirmDelete(true)}
                  disabled={isSystemRole}
                >
                  Eliminar rol
                </Button>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography className="admin-user-delete-confirm">Confirma la eliminación de {selectedRole.name}.</Typography>
                  <Button variant="outlined" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancelar</Button>
                  <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Eliminando...' : 'Confirmar eliminación'}
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
          <Button type="submit" variant="contained" disabled={submitting || loading || loadingOptions}>
            {submitting ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export const AdminRolesPermissions = () => {
  const { session } = useAuth()
  const adminCode = getAdminApplicationCode(session)
  const canReadPermissions = tienePermiso(session, adminCode, 'PERMISSIONS_READ')
  const canWritePermissions = tienePermiso(session, adminCode, 'PERMISSIONS_WRITE')
  const [activeTab, setActiveTab] = React.useState('roles')
  const [roleSort, setRoleSort] = React.useState({ column: 'name', direction: 'asc' })
  const [roles, setRoles] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [selectedRole, setSelectedRole] = React.useState(null)
  const [loadingRoleDetail, setLoadingRoleDetail] = React.useState(false)
  const [roleDetailError, setRoleDetailError] = React.useState('')
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)

  const loadRoles = React.useCallback(async () => {
    setLoading(true)
    setErrorMessage('')

    try {
      const response = await getRoles({ page: 1, limit: 500, sortDirection: 'asc' })
      setRoles(getPaginatedItems(response).map(mapApiRole))
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined
    loadRoles()
    return undefined
  }, [loadRoles])

  const handleRoleClick = async (role) => {
    setSelectedRole(role)
    setRoleDetailError('')

    if (!role.id || API_REQUESTS_PAUSED) return

    setLoadingRoleDetail(true)
    try {
      const response = await getRole(role.id)
      const detail = response?.data || response
      if (detail?.id) setSelectedRole(mapApiRole(detail))
    } catch (error) {
      setRoleDetailError(getApiErrorMessage(error, 'No se ha podido cargar el detalle del rol.'))
    } finally {
      setLoadingRoleDetail(false)
    }
  }

  const handleCreateRole = async (payload) => {
    const response = await createRole(payload)
    const createdRole = response?.data || response

    if (createdRole?.id) {
      setRoles((currentRoles) => [mapApiRole(createdRole), ...currentRoles])
      return
    }

    await loadRoles()
  }

  const handleUpdateRole = async (id, payload) => {
    const response = await updateRole(id, payload)
    const updatedRole = response?.data || response

    if (updatedRole?.id) {
      const mappedRole = mapApiRole(updatedRole)
      setRoles((currentRoles) => currentRoles.map((role) => (
        String(role.id) === String(id) ? mappedRole : role
      )))
      setSelectedRole(mappedRole)
      return
    }

    await loadRoles()
    const detail = await getRole(id)
    setSelectedRole(mapApiRole(detail?.data || detail))
  }

  const handleDeleteRole = async (id) => {
    await deleteRole(id)
    setRoles((currentRoles) => currentRoles.filter((role) => String(role.id) !== String(id)))
    setSelectedRole(null)
  }

  const metrics = React.useMemo(() => buildRoleMetrics(roles), [roles])
  const sortedRoles = React.useMemo(() => sortTableRows(roles, roleSort.column, roleSort.direction, {
    name: (role) => role.name,
    code: (role) => role.code,
    application: (role) => role.app,
    users: (role) => Number(role.users),
    permissions: (role) => Number(role.permissions),
    scope: (role) => role.scope,
  }), [roles, roleSort])

  return (
    <Box className="page-fade">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Roles y permisos</Typography>
          <Typography>Define perfiles de acceso, permisos por aplicación y alcance operativo por almacén.</Typography>
        </Box>
        {activeTab === 'roles' && <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateDialogOpen(true)}>Nuevo rol</Button>}
      </Box>

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Roles y permisos" sx={{ mb: 2 }}>
        <Tab value="roles" label="Roles" />
        <Tab value="permissions" label="Permisos" />
      </Tabs>

      {activeTab === 'roles' && <>
      <Grid container spacing={2.1}>
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Typography className="section-title">Roles configurados</Typography>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeader column="name" label="Rol" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <SortableHeader column="code" label="Código" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <SortableHeader column="application" label="Aplicación" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <SortableHeader column="users" label="Usuarios" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <SortableHeader column="permissions" label="Permisos" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <SortableHeader column="scope" label="Alcance" sort={roleSort} onSort={(column) => setRoleSort((current) => toggleTableSort(current, column))} />
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRoles.map((role) => (
                <TableRow
                  component={AnimatedTableRow}
                  key={role.id || role.code}
                  hover
                  className="admin-clickable-row"
                  onClick={() => handleRoleClick(role)}
                >
                  <TableCell>
                    <Box className="activity-app-cell">
                      <Box className="activity-row-icon">{role.icon}</Box>
                      <Box>
                        <Typography>{role.name}</Typography>
                        <Typography className="admin-table-subtext">{role.description}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{role.code}</TableCell>
                  <TableCell>{role.app}</TableCell>
                  <TableCell>{role.users}</TableCell>
                  <TableCell><Chip size="small" className="suite-status-chip" label={`${role.permissions} permisos`} /></TableCell>
                  <TableCell>{role.scope}</TableCell>
                  <TableCell align="right"><ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></TableCell>
                </TableRow>
              ))}
              {!loading && roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography className="admin-user-empty">No hay roles configurados.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>
      </>}

      {activeTab === 'permissions' && <AdminPermissions canRead={canReadPermissions} canWrite={canWritePermissions} />}

      <CreateRoleDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateRole={handleCreateRole}
      />

      <RoleDetailDialog
        role={selectedRole}
        open={Boolean(selectedRole)}
        loading={loadingRoleDetail}
        errorMessage={roleDetailError}
        onClose={() => setSelectedRole(null)}
        onUpdateRole={handleUpdateRole}
        onDeleteRole={handleDeleteRole}
      />
    </Box>
  )
}
